import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { generatePath, useParams } from 'react-router'

import { useLandingHeaderVariant } from '@pages/landing/model/useLandingHeaderVariant'

import { useLandingPage } from '@entities/LandingPage'

import { LandingPageBuilder } from '@features/landing-page-builder'


import NotFoundComponent from '@widgets/not-found/NotFoundComponent'

import SEO from '@shared/components/SEO'
import { URL_LANDING } from '@shared/contants'
// Direct path, not the barrel: it re-exports PromoSubdomainRoot, which renders
// this page — importing it here would close an import cycle.
import { getPromoLandingCode } from '@shared/lib/promo-subdomain/promoHost'
import Container from '@shared/ui/container'
import Wrapper from '@shared/ui/wrapper'

import styles from './styles.module.scss'

function LandingSkeleton () {
  return (
    <Container>
      <Wrapper className={styles.wrapper}>
        <SkeletonTheme>
          <Skeleton height={190} style={{ borderRadius: 24, marginBottom: 24 }} />
          <Skeleton width="40%" height={28} style={{ marginBottom: 16 }} />
          <div className={styles.skeleton__row}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} height={180} style={{ borderRadius: 16 }} />
            ))}
          </div>
        </SkeletonTheme>
      </Wrapper>
    </Container>
  )
}

/*
  Renders a backend-configured landing page by its code (/landing/:code).

  The page is a thin shell on purpose: the backend decides which blocks exist,
  in what order and for which state, and the block mapper renders them. Adding a
  block type is a backend change plus a registry entry — not a new page.
*/
export default function LandingPage () {
  const { code: codeParam } = useParams<{ code: string }>()

  /*
    Two ways in: /landing/:code, or the root of a promo subdomain, where the
    code is the host's first label and the path carries no param.
  */
  const code = codeParam ?? getPromoLandingCode() ?? undefined

  const { landingPage, loading } = useLandingPage({ code })

  // Swaps the layout chrome for what the landing asks for (custom / none / site
  // header) and restores it on leave. Runs before the early returns — hooks
  // cannot be conditional.
  useLandingHeaderVariant(landingPage?.header, Boolean(landingPage))

  // First load, nothing cached yet.
  if (loading && !landingPage) {
    return <LandingSkeleton />
  }

  // No such landing, or it is no longer active.
  if (!landingPage) {
    return (
      <Container>
        <Wrapper className={styles.wrapper}>
          <NotFoundComponent />
        </Wrapper>
      </Container>
    )
  }

  const { name, blocks, actions, header, promo } = landingPage

  // A hero-style banner block already opens the page with a headline.
  const hasHeroBlock = blocks.some(
    (block) => block.type === 'banner' && Boolean((block.payload as { title?: string } | null)?.title),
  )
  const showPageTitle = header?.variant !== 'custom' && !hasHeroBlock

  return (
    <Container>
      {/*
        The same landing is reachable at /landing/<code> and at the root of its
        promo subdomain, so it needs one declared canonical. /landing/<code> on
        the main site is that form — getBaseUrl() resolves the host.
      */}
      {name && (
        <SEO
          title={name}
          description={name}
          canonicalUrl={code ? generatePath(URL_LANDING, { code }) : undefined}
        />
      )}

      <Wrapper className={styles.wrapper}>
        {/*
          The landing's name as a page title. Skipped for the custom header,
          which already carries the landing's identity as its logo, and when a
          hero block opens the page with its own headline.
        */}
        {showPageTitle && name && <h1 className={styles.title}>{name}</h1>}

        <LandingPageBuilder blocks={blocks} actions={actions} promo={promo} />
      </Wrapper>
    </Container>
  )
}
